import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import JsCookies from 'js-cookie'

import { useDocument } from '@tribeplatform/react-ui-kit/hooks'

import { Appearance, AppInitialProps, ColorMode } from '../../@types/index.js'
import { COOKIE_APPEARANCE } from '../../server/constants/cookie.constants.js'
import { applyDarkMode } from './utils.js'

const setAppearanceCookie = (appearance: Appearance): void => {
  try {
    JsCookies.set(COOKIE_APPEARANCE, JSON.stringify(appearance))
  } catch (err) {
    logger.error('set appearance into cookie error', err)
  }
}

const DEFAULT_APPEARANCE: Appearance = {
  colorMode: 'light',
  syncColorMode: false,
}

export type AppearanceProps = {
  darkMode: boolean
  appearance: Appearance
  setDarkMode: Dispatch<SetStateAction<boolean>>
  toggleDarkMode: () => void
  setAppearance: (appearance: Appearance) => void
}

export const AppearanceContext = createContext<AppearanceProps>({
  darkMode: false,
  appearance: DEFAULT_APPEARANCE,
  setDarkMode: () => undefined,
  toggleDarkMode: () => undefined,
  setAppearance: () => undefined,
})

export const useAppearance = (): AppearanceProps => {
  const context = useContext(AppearanceContext)
  return context
}

export type AppearanceProviderProps = {
  customDarkMode?: boolean
  appearance?: AppInitialProps['appearance']
  children?: ReactNode
}

export const AppearanceProvider: FC<AppearanceProviderProps> = ({
  children,
  appearance,
}) => {
  const userDefinedSettings = !!appearance
  const [appearanceState, setAppearanceState] = useState<Appearance>(
    appearance || DEFAULT_APPEARANCE,
  )
  const { document } = useDocument()

  const { syncColorMode, colorMode } = appearanceState

  const setAppearance = useCallback((appearance: Appearance) => {
    setAppearanceCookie(appearance)
    setAppearanceState(appearance)
  }, [])

  const setMode = useCallback(
    (colorMode: ColorMode) => {
      const app: Appearance = {
        ...appearanceState,
        colorMode,
      }
      setAppearance(app)
    },
    [appearanceState, setAppearance],
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = event => {
      if (userDefinedSettings && syncColorMode) {
        setMode(event.matches ? 'dark' : 'light')
      }
    }

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', handler)
    } else {
      mediaQuery.addListener(handler)
    }

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', handler)
      } else {
        mediaQuery.removeListener(handler)
      }
    }
  }, [appearanceState, userDefinedSettings])

  useEffect(() => {
    applyDarkMode(document.documentElement, colorMode === 'dark')
  }, [colorMode])

  return (
    <AppearanceContext.Provider
      value={{
        darkMode: colorMode === 'dark',
        appearance: appearanceState,
        setDarkMode: isDark => {
          setMode(isDark ? 'dark' : 'light')
        },
        toggleDarkMode: () => {
          setMode(colorMode === 'dark' ? 'light' : 'dark')
        },
        setAppearance,
      }}
    >
      {children}
    </AppearanceContext.Provider>
  )
}
